import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { formatearMoneda } from "../../config/moneda";
import {
  aCentavos,
  esCuentaCorriente,
  filtrarFormasPagoConsumidorFinal,
  MAX_PAGOS,
  validarPagos,
} from "../../servicios/pagosVenta";
import { useStorePos } from "./useStorePos";

const etiquetaFormaPago = (formaPago, t) =>
  esCuentaCorriente(formaPago) ? t("pos.leaveOnAccount") : formaPago?.sDescripcion || "";

const PosPagosEditor = ({ total }) => {
  const { t } = useTranslation();
  const [modalAbierto, setModalAbierto] = useState(false);
  const cerrarRef = useRef(null);
  const pagos = useStorePos((state) => state.Pagos);
  const catalogo = useStorePos((state) => state.ListaFormasPago);
  const consumidorFinalGenerico = useStorePos((state) => state.ModoCliente === "consumidorFinal");
  const buscando = useStorePos((state) => state.BuscandoFormasPago);
  const errorCatalogo = useStorePos((state) => state.ErrorFormasPago);
  const formaFiscalId = useStorePos((state) => state.FormaFiscalPrincipalId);
  const confirmando = useStorePos((state) => state.ConfirmandoVenta);
  const catalogoDisponible = filtrarFormasPagoConsumidorFinal(catalogo, consumidorFinalGenerico);
  const validacion = validarPagos(pagos, total);
  const pagosConClasificacion = pagos.map((pago) => ({
    pago,
    cuentaCorriente: esCuentaCorriente(
      catalogo.find((forma) => forma.Id === Number(pago.FormaPagoId)) || pago
    ),
  }));
  const cuentaCorrienteCentavos = pagosConClasificacion.reduce(
    (suma, item) => suma + (item.cuentaCorriente ? Math.max(0, aCentavos(item.pago.Importe) || 0) : 0),
    0
  );
  const cobradoCentavos = Math.max(0, validacion.asignadoCentavos - cuentaCorrienteCentavos);

  const editar = (clave, campo, valor) => useStorePos.getState().actualizarPago(clave, campo, valor);
  const cerrarModal = useCallback(() => {
    if (!confirmando) setModalAbierto(false);
  }, [confirmando]);

  useEffect(() => {
    if (!modalAbierto) return undefined;
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => cerrarRef.current?.focus(), 0);
    const cerrarConEscape = (event) => {
      if (event.key === "Escape") cerrarModal();
    };
    window.addEventListener("keydown", cerrarConEscape);
    return () => {
      document.body.style.overflow = overflowAnterior;
      window.removeEventListener("keydown", cerrarConEscape);
    };
  }, [cerrarModal, modalAbierto]);

  const descripcionPagos = pagos.length === 0
    ? t("pos.noPaymentsConfigured")
    : pagos.length === 1
      ? (pagosConClasificacion[0]?.cuentaCorriente ? t("pos.onAccount") : pagos[0].FormaPago || t("pos.paymentMethodPending"))
      : t("pos.assignmentCount", { count: pagos.length });

  const modal = modalAbierto ? (
    <div className="pos-pagos-modal-fondo" onMouseDown={(event) => {
      if (event.target === event.currentTarget) cerrarModal();
    }}>
      <section className="pos-pagos-modal" role="dialog" aria-modal="true" aria-labelledby="pos-pagos-modal-titulo">
        <header className="pos-pagos-modal-header">
          <div>
            <span className="pos-step">{t("pos.saleCheckout")}</span>
            <h2 id="pos-pagos-modal-titulo">{t("pos.configureCheckout")}</h2>
            <p>{t("pos.distributePayment", { amount: formatearMoneda(total) })}</p>
          </div>
          <button ref={cerrarRef} type="button" className="pos-pagos-modal-cerrar" onClick={cerrarModal}
            disabled={confirmando} aria-label={t("pos.closePaymentSettings")}>
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        </header>

        <div className="pos-pagos-modal-body">
          {errorCatalogo && <div className="pos-pagos-modal-error" role="alert">{errorCatalogo}</div>}
          <div className="pos-pagos-modal-acciones">
            <div>
              <strong>{t("pos.assignmentCount", { count: pagos.length })}</strong>
              <small>{t("pos.maximumPayments", { count: MAX_PAGOS })}</small>
            </div>
            <button type="button" className="pos-pago-agregar" disabled={pagos.length >= MAX_PAGOS || confirmando}
              onClick={() => useStorePos.getState().agregarPago(total)}>
              <i className="pi pi-plus" aria-hidden="true" /> {t("pos.addPaymentMethod")}
            </button>
          </div>

          <div className="pos-pagos-lista">
            {pagosConClasificacion.map(({ pago, cuentaCorriente }, indice) => {
              const errorId = `pago-${pago.ClaveUi}-errores`;
              const errores = validacion.erroresPorClave[pago.ClaveUi] || [];
              return (
                <fieldset className="pos-pago-fila" key={pago.ClaveUi} aria-describedby={errores.length ? errorId : undefined}>
                  <legend><span>{indice + 1}</span> {t("pos.assignment", { number: indice + 1 })}</legend>
                  <div className="pos-pago-fila-campos">
                    <label className="pos-pago-forma">
                      <span>{t("pos.paymentMethodOrCondition")}</span>
                      <select value={pago.FormaPagoId || ""} disabled={buscando || confirmando}
                        onChange={(event) => editar(pago.ClaveUi, "FormaPagoId", event.target.value)}>
                        <option value="">{buscando ? t("common.loading") : t("pos.selectOption")}</option>
                        {catalogoDisponible.map((forma) => <option key={forma.Id} value={forma.Id}>{etiquetaFormaPago(forma, t)}</option>)}
                      </select>
                    </label>
                    <label>
                      <span>{t("pos.amount")}</span>
                      <input type="number" inputMode="decimal" min="0.01" step="0.01" value={pago.Importe}
                        disabled={confirmando} onChange={(event) => editar(pago.ClaveUi, "Importe", event.target.value)} />
                    </label>
                    {!cuentaCorriente && <label>
                        <span>{t("pos.receivedAmount")} <small>({t("common.optional").toLowerCase()})</small></span>
                        <input type="number" inputMode="decimal" min="0" step="0.01" value={pago.MontoRecibido}
                          disabled={confirmando} onChange={(event) => editar(pago.ClaveUi, "MontoRecibido", event.target.value)} placeholder="0.00" />
                      </label>}
                    {!cuentaCorriente && <label className="pos-pago-vuelto">
                        <span>{t("pos.change")}</span>
                        <output>{formatearMoneda(pago.Vuelto)}</output>
                      </label>}
                    {cuentaCorriente && <div className="pos-pago-cuenta-corriente" role="note">
                      <i className="pi pi-book" aria-hidden="true" />
                      <span><strong>{t("pos.customerDebtNotice")}</strong> {t("pos.noCashIncomeOrChange")}</span>
                    </div>}
                    <label className="pos-pago-referencia">
                      <span>{t("pos.reference")} <small>({t("common.optional").toLowerCase()})</small></span>
                      <input type="text" maxLength="201" value={pago.Referencia} disabled={confirmando}
                        onChange={(event) => editar(pago.ClaveUi, "Referencia", event.target.value)}
                        placeholder={t("pos.referencePlaceholder")} />
                      <small>{String(pago.Referencia || "").length}/200</small>
                    </label>
                  </div>
                  <button type="button" className="pos-pago-quitar" disabled={pagos.length === 1 || confirmando}
                    onClick={() => useStorePos.getState().eliminarPago(pago.ClaveUi)} aria-label={t("pos.removePayment", { number: indice + 1 })}>
                    <i className="pi pi-trash" aria-hidden="true" /> <span>{t("pos.remove")}</span>
                  </button>
                  {errores.length > 0 && (
                    <ul className="pos-pago-errores" id={errorId} role="alert">
                      {errores.map((error) => <li key={error}>{error}</li>)}
                    </ul>
                  )}
                </fieldset>
              );
            })}
          </div>

          {pagos.length > 1 && (
            <label className="pos-forma-fiscal-principal">
              <span>{t("pos.mainFiscalMethod")}</span>
              <select value={formaFiscalId || ""} disabled={confirmando}
                onChange={(event) => useStorePos.getState().setFormaFiscalPrincipal(event.target.value)}>
                <option value="">{t("pos.selectForInvoice")}</option>
                {catalogoDisponible.map((forma) => <option key={forma.Id} value={forma.Id}>{etiquetaFormaPago(forma, t)}</option>)}
              </select>
              <small>{t("pos.mainFiscalMethodHelp")}</small>
              {!formaFiscalId && <small className="pos-linea-error" role="alert">{t("pos.requiredForCombinedPayments")}</small>}
            </label>
          )}
        </div>

        <footer className="pos-pagos-modal-footer">
          <div className="pos-pagos-totales" aria-live="polite">
            <span>{t("common.total")} <strong>{formatearMoneda(validacion.total)}</strong></span>
            <span>{t("pos.collected")} <strong>{formatearMoneda(cobradoCentavos / 100)}</strong></span>
            {cuentaCorrienteCentavos > 0 && <span className="cuenta-corriente">
              {t("pos.onAccount")} <strong>{formatearMoneda(cuentaCorrienteCentavos / 100)}</strong>
            </span>}
            <span className={validacion.diferenciaCentavos === 0 ? "completo" : "pendiente"}>
              {validacion.diferenciaCentavos >= 0 ? t("pos.unassigned") : t("pos.exceeded")}
              <strong>{formatearMoneda(Math.abs(validacion.diferencia))}</strong>
            </span>
          </div>
          {validacion.erroresPorClave.total?.map((error) => (
            <small className="pos-linea-error pos-pagos-error-total" role="alert" key={error}>{error}</small>
          ))}
          <div className="pos-pagos-modal-footer-acciones">
            <button type="button" className="pos-pagos-cancelar" onClick={cerrarModal} disabled={confirmando}>{t("common.close")}</button>
            <button type="button" className="pos-pagos-aplicar" onClick={cerrarModal}
              disabled={!validacion.valido || (pagos.length > 1 && !formaFiscalId) || confirmando}>
              <i className="pi pi-check" aria-hidden="true" /> {t("pos.apply")}
            </button>
          </div>
        </footer>
      </section>
    </div>
  ) : null;

  return (
    <div className={`pos-pagos-resumen-compacto ${validacion.valido ? "completo" : "incompleto"}`}>
      <div className="pos-pagos-resumen-icono"><i className="pi pi-wallet" aria-hidden="true" /></div>
      <div className="pos-pagos-resumen-info">
        <span>{t("pos.saleCheckout")}</span>
        <strong>{descripcionPagos}</strong>
        <small>
          {validacion.valido
            ? (cuentaCorrienteCentavos > 0
              ? t("pos.amountOnAccount", { amount: formatearMoneda(cuentaCorrienteCentavos / 100) })
              : t("pos.amountCollected", { amount: formatearMoneda(validacion.asignado) }))
            : t("pos.amountPending", { amount: formatearMoneda(Math.abs(validacion.diferencia)) })}
        </small>
      </div>
      {pagos.length > 1 && <span className="pos-pagos-resumen-chip">{t("pos.combinedPayment")}</span>}
      <button type="button" className="pos-pagos-configurar" onClick={() => setModalAbierto(true)} disabled={confirmando}>
        <i className="pi pi-pencil" aria-hidden="true" />
        {pagos.length ? t("pos.configureCheckout") : t("pos.choosePaymentMethod")}
      </button>
      {typeof document !== "undefined" && createPortal(modal, document.body)}
    </div>
  );
};

export default PosPagosEditor;
